import { useMemo } from 'react'
import * as S from './NotificationItem.styles'
import Markdown from 'react-markdown'
import moment from 'moment'
import useApiService from 'services/api'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { User } from 'models/user'
import notificationAvatar from 'assets/notification-avatar.png'
import { Notification } from 'models/notifications'
import DOMPurify from 'dompurify'

export type NotificationItemProps = {
  isUnread?: boolean
  notification: Notification
  onClick?: () => void
}

const NotificationAvatar = ({ fromUser }: { fromUser?: User }) => {
  const userInitials = useMemo(() => {
    if (!fromUser) return ''
    const { first_name: firstName, last_name: lastName } = fromUser
    return `${firstName[0]}${lastName[0]}`.toLocaleUpperCase()
  }, [fromUser])

  if (fromUser) {
    return <S.NotificationAvatar>{userInitials}</S.NotificationAvatar>
  }
  return (
    <S.NotificationAvatar>
      <img src={notificationAvatar} alt="notification-avatar" />
    </S.NotificationAvatar>
  )
}

export const NotificationItem = ({
  isUnread = false,
  notification,
  onClick,
}: NotificationItemProps) => {
  const userName = useMemo(() => {
    if (notification.from_user) {
      const { first_name: firstName, last_name: lastName } =
        notification.from_user
      return `${firstName} ${lastName}`
    }
    return ''
  }, [notification])

  const api = useApiService()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleClick = () => {
    if (isUnread) {
      api.markNotificationAsRead([notification.id]).then(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
        queryClient.invalidateQueries({ queryKey: ['unread-notifications'] })
      })
    }
    if (notification.notification_link) {
      navigate(notification.notification_link)
    }
    if (onClick) {
      onClick()
    }
  }

  const notificationMessage = useMemo(() => {
    // Extract any legacy content first (NOTE_MENTIONED with |||)
    let mainMessage = `${userName} ${notification.message}`
    let legacyCardContent: string | null = null

    if (notification.message.includes('|||')) {
      const parts = notification.message.split('|||')
      mainMessage = `${userName} ${parts[0]}`
      legacyCardContent = DOMPurify.sanitize(parts[1])
    }

    // Determine if we should render content in a card
    const cardContent = notification.metadata?.card_content || legacyCardContent

    // Determine if message should be split by newlines (for multi-line messages)
    const shouldSplitLines =
      notification.event === 'REMINDER' || mainMessage.includes('\n')

    // Render the main message
    const renderedMessage = (
      <S.NotificationText>
        {shouldSplitLines ? (
          // Split multi-line messages and render each line
          mainMessage
            .split('\n')
            .filter((line) => line.trim())
            .map((line, index) => (
              <div key={index}>
                <Markdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {line}
                </Markdown>
              </div>
            ))
        ) : (
          // Single line message
          <Markdown
            components={{
              p: ({ children }) => <>{children}</>,
            }}
          >
            {mainMessage}
          </Markdown>
        )}
      </S.NotificationText>
    )

    // If there's card content (from metadata or legacy), render it below
    if (cardContent) {
      return (
        <>
          {renderedMessage}
          <div className="mt-2 py-2 px-3 bg-gray-50 rounded-md border border-gray-200">
            <div className="text-sm text-gray-700">
              {typeof cardContent === 'string' && cardContent.includes('<') ? (
                <div dangerouslySetInnerHTML={{ __html: cardContent }} />
              ) : (
                cardContent
              )}
            </div>
          </div>
        </>
      )
    }

    return renderedMessage
  }, [notification, userName])

  return (
    <S.NotificationItem
      isUnread={isUnread}
      onClick={handleClick}
      hasLink={!!notification.notification_link}
    >
      <S.NotificationLeft>
        {isUnread && <S.UnreadDot />}
        <NotificationAvatar fromUser={notification.from_user} />
      </S.NotificationLeft>
      <S.NotificationRight>
        {notificationMessage}
        <S.NotificationTime>
          {moment(notification.created).fromNow()}
        </S.NotificationTime>
      </S.NotificationRight>
    </S.NotificationItem>
  )
}
